/**
 * Utility functions for parsing and transforming behavioral traits data
 */

export interface ParsedTrait {
  name: string;
  score: number;
  category: string;
  displayName: string;
}

/**
 * Parse raw traits object from SSE response into structured array
 * 
 * Input: {
 *   collaboration_score: 0.215,
 *   collaboration_category: "weak",
 *   leadership_potential_score: 0.54,
 *   leadership_potential_category: "neutral"
 * }
 * 
 * Output: [
 *   { name: 'collaboration', score: 0.215, category: 'weak', displayName: 'Collaboration' },
 *   { name: 'leadership_potential', score: 0.54, category: 'neutral', displayName: 'Leadership Potential' }
 * ]
 */
export function parseTraits(rawTraits: Record<string, any>): ParsedTrait[] {
  if (!rawTraits || typeof rawTraits !== 'object') {
    return [];
  }

  const traitMap = new Map<string, Partial<ParsedTrait>>();

  // Iterate through all keys and group by trait name
  Object.entries(rawTraits).forEach(([key, value]) => {
    if (key.endsWith('_score')) {
      const traitName = key.replace('_score', '');
      const existing = traitMap.get(traitName) || {};
      traitMap.set(traitName, { ...existing, name: traitName, score: Number(value) });
    } else if (key.endsWith('_category')) {
      const traitName = key.replace('_category', '');
      const existing = traitMap.get(traitName) || {};
      traitMap.set(traitName, { ...existing, name: traitName, category: String(value) });
    }
  });

  // Convert map to array and add display names
  const traits: ParsedTrait[] = [];
  traitMap.forEach((trait, name) => {
    if (trait.score !== undefined && trait.category !== undefined) {
      traits.push({
        name,
        score: trait.score,
        category: trait.category,
        displayName: formatTraitName(name)
      });
    }
  });

  return traits;
}

/**
 * Format trait name for display
 * 'collaboration' -> 'Collaboration'
 * 'leadership_potential' -> 'Leadership Potential'
 */
function formatTraitName(name: string): string {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get color class for trait category
 */
export function getCategoryColor(category: string): string {
  const lowerCategory = category.toLowerCase();
  
  switch (lowerCategory) {
    case 'strong':
      return 'text-green-600 bg-green-50';
    case 'neutral':
    case 'moderate':
      return 'text-blue-600 bg-blue-50';
    case 'weak':
      return 'text-amber-600 bg-amber-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
}

/**
 * Get category icon
 */
export function getCategoryIcon(category: string): string {
  const lowerCategory = category.toLowerCase();
  
  switch (lowerCategory) {
    case 'strong':
      return 'trending_up';
    case 'neutral':
    case 'moderate':
      return 'trending_flat';
    case 'weak':
      return 'trending_down';
    default:
      return 'remove';
  }
}

